import type { Observable } from 'rxjs';
import type { JssStyle } from 'jss';
import type { Widgets } from '@common/enums';
import type { Uuid } from '@common/types';
import type { SelectedFileExtended } from '@common/types/file.type';
import type { WebLegacyWidgetEntity, WebWidgetEntity } from '@web/types';

import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';

import { isLegacyWidget } from '@common/helpers/widget-envelope';

import { WebWidgetsFactoryService } from '@web/core/services/web-widgets-factory.service';

import WidgetsLegacyActions, { WidgetsActions } from './widgets.actions';
import * as WidgetsSelectors from './widgets.selectors';

@Injectable()
export class WidgetsFacade {
    private readonly store = inject(Store);

    public readonly widgetsIds$ = this.store.select(WidgetsSelectors.selectWidgetsIds);
    public readonly widgets$ = this.store.select(WidgetsSelectors.selectAllWidgets);
    public readonly widgetsEntities$ = this.store.select(WidgetsSelectors.selectWidgetsEntities);

    public init(widgets: WebLegacyWidgetEntity[]): void {
        this.store.dispatch(WidgetsLegacyActions.init({ widgets }));
    }

    /** Re-insert without a history entry (undo of a removal, redo of an add). */
    public addWidget(widget: WebLegacyWidgetEntity | WebWidgetEntity, index: number): void {
        if (isLegacyWidget(widget)) {
            this.store.dispatch(WidgetsLegacyActions.addWidget({ widget, index }));
            return;
        }

        this.store.dispatch(WidgetsActions.added({ widget, index }));
    }

    public addMany(entities: WebLegacyWidgetEntity[]): void {
        this.store.dispatch(WidgetsLegacyActions.addManyWidgets({ widgets: entities }));
    }

    public addWidgetToGrid(
        widget: WebLegacyWidgetEntity | WebWidgetEntity,
        index: number,
        gridCellIndex: number
    ): void {
        if (isLegacyWidget(widget)) {
            this.store.dispatch(WidgetsLegacyActions.addWidgetToGrid({ widget, gridCellIndex, index }));
            return;
        }

        this.store.dispatch(WidgetsActions.added({ widget, index, gridCellIndex }));
    }

    public createWidget(columnId: Uuid, index: number, widget: Widgets, meta?: any): void {
        this.store.dispatch(WidgetsLegacyActions.createWidget({ columnId, index, widget, meta }));
    }

    public createWidgetToGrid(
        columnId: Uuid,
        gridId: Uuid,
        gridCellIndex: number,
        index: number,
        widget: Widgets
    ): void {
        this.store.dispatch(
            WidgetsLegacyActions.createWidgetToGrid({ columnId, gridId, gridCellIndex, index, widget })
        );
    }

    // public updateWidgetProperties<T extends WebWidgetDataUnion | Partial<WebWidgetDataUnion>>(
    //     id: Uuid,
    //     changes: Partial<WebLegacyWidgetEntity<T>>
    // ): void {
    public updateLegacyWidgetProperties(id: Uuid, changes: Partial<WebLegacyWidgetEntity>): void {
        this.store.dispatch(WidgetsLegacyActions.updateWidgetProperties({ update: { id, changes } }));
    }

    /** Merges into `props` / `meta`. */
    public updateWidgetProperties(id: Uuid, changes: Partial<WebWidgetEntity>): void {
        this.store.dispatch(WidgetsActions.updateProperties({ update: { id, changes } }));
    }

    /** A write the user did not make (`transactWithoutHistory`): into the store, without saving the page (#75695). */
    public syncLegacyWidgetProperties(id: Uuid, changes: Partial<WebLegacyWidgetEntity>): void {
        this.store.dispatch(WidgetsLegacyActions.syncWidgetProperties({ update: { id, changes } }));
    }

    public updateZeroWidgetProperties(id: Uuid, changes: Partial<WebLegacyWidgetEntity>): void {
        this.store.dispatch(WidgetsLegacyActions.updateZeroWidgetProperties({ update: { id, changes } }));
    }

    public updateWidgetDisplayId(id: Uuid, displayId: string): void {
        this.store.dispatch(WidgetsLegacyActions.updateWidgetDisplayId({ id, displayId }));
    }

    public updateWidgetData<T>(id: Uuid, data: Partial<T>): void {
        this.store.dispatch(WidgetsLegacyActions.updateWidgetData({ update: { id, changes: { data } } }));
    }

    public updateGalleryImageData(id: Uuid, image: SelectedFileExtended, index: number) {
        this.store.dispatch(WidgetsLegacyActions.updateGalleryImageData({ id, image, index }));
    }

    public updateProductImageData(id: Uuid, image: SelectedFileExtended) {
        this.store.dispatch(WidgetsLegacyActions.updateProductImageData({ id, image }));
    }

    public updateWidgetStyles(id: Uuid, styles: JssStyle, mobileStyles?: JssStyle): void {
        this.store.dispatch(
            WidgetsLegacyActions.updateWidgetStyles({ update: { id, changes: { styles, mobileStyles } } })
        );
    }

    public moveWidgetToColumn(
        widgetId: Uuid,
        prevColumnId: Uuid,
        currentColumnId: Uuid,
        previousIndex: number,
        currentIndex: number,
        skipHistory: boolean = false
    ): void {
        this.store.dispatch(
            WidgetsLegacyActions.widgetMoved({
                widgetId,
                prevColumnId,
                currentColumnId,
                previousIndex,
                currentIndex,
                skipHistory,
            })
        );
    }

    public copyWidget(widget: WebLegacyWidgetEntity | WebWidgetEntity, index: number): void {
        if (isLegacyWidget(widget)) {
            this.store.dispatch(
                WidgetsLegacyActions.copyWidget({
                    widget: WebWidgetsFactoryService.copyWidget<WebLegacyWidgetEntity>(widget),
                    index,
                })
            );
            return;
        }

        this.store.dispatch(WidgetsActions.copied({ widget: WebWidgetsFactoryService.copyWidget(widget), index }));
    }

    public copyZeroWidget(widget: WebLegacyWidgetEntity, index: number): void {
        this.store.dispatch(
            WidgetsLegacyActions.copyZeroWidget({
                widget: WebWidgetsFactoryService.copyWidget<WebLegacyWidgetEntity>(widget),
                index,
            })
        );
    }

    public copyWidgetToGrid(
        widget: WebLegacyWidgetEntity | WebWidgetEntity,
        index: number,
        gridCellIndex: number,
        skipHistory: boolean = false
    ): void {
        if (isLegacyWidget(widget)) {
            this.store.dispatch(
                WidgetsLegacyActions.copyWidgetToGrid({
                    widget: WebWidgetsFactoryService.copyWidget<WebLegacyWidgetEntity>(widget),
                    index,
                    gridCellIndex,
                    skipHistory,
                })
            );
            return;
        }

        this.store.dispatch(
            WidgetsActions.copied({
                widget: WebWidgetsFactoryService.copyWidget(widget),
                index,
                gridCellIndex,
                skipHistory,
            })
        );
    }

    public removeWidget(
        widget: WebLegacyWidgetEntity | WebWidgetEntity,
        index: number,
        skipHistory: boolean = false,
        skipUnselectEntity: boolean = false
    ): void {
        if (isLegacyWidget(widget)) {
            this.store.dispatch(WidgetsLegacyActions.removeWidget({ widget, index, skipHistory, skipUnselectEntity }));
            return;
        }

        this.store.dispatch(WidgetsActions.removed({ widget, index, skipHistory, skipUnselectEntity }));
    }

    public removeWidgetFromGrid(
        widget: WebLegacyWidgetEntity | WebWidgetEntity,
        index: number,
        gridCellIndex: number,
        skipHistory: boolean = false
    ): void {
        if (isLegacyWidget(widget)) {
            this.store.dispatch(
                WidgetsLegacyActions.removeWidgetFromGrid({ widget, index, gridCellIndex, skipHistory })
            );
            return;
        }

        this.store.dispatch(WidgetsActions.removed({ widget, index, gridCellIndex, skipHistory }));
    }

    public removeManyWidgets(widgets: WebLegacyWidgetEntity[]): void {
        this.store.dispatch(
            WidgetsLegacyActions.removeManyWidgets({
                widgetsIds: widgets.map((widget: WebLegacyWidgetEntity) => widget.id),
            })
        );
    }

    public widgetById$(id: Uuid): Observable<WebLegacyWidgetEntity | undefined> {
        return this.store.select(WidgetsSelectors.selectWidgetById(id));
    }
}
